import type { Middleware, PayloadAction, Reducer } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import type { UserTermsStatus } from "types/users.types"
import type { TWarningPassword, TFailurePassword } from "types/password.types"
import type { RootState } from "."

interface AuthState {
  user: string | null
  token: string | null
  tokenExpirationAt: string | null
  code?: TWarningPassword | TFailurePassword
  globalRole: string | null
  otpCode?: string
  terms: UserTermsStatus | null
}

const initialState: AuthState = {
  user: null,
  token: null,
  tokenExpirationAt: null,
  code: undefined,
  globalRole: null,
  otpCode: undefined,
  terms: null,
}

function getPersistentAuth(): AuthState {
  // Lazy load the state from the localStorage or fallback to the initialState
  try {
    const authState = JSON.parse(localStorage.getItem("auth") as string)
    const { user, token, tokenExpirationAt, code, globalRole, otpCode, terms } = authState
    return {
      user,
      token,
      tokenExpirationAt,
      code,
      globalRole: globalRole ?? initialState.globalRole,
      otpCode,
      terms,
    }
  } catch (_error) {
    return initialState
  }
}

type SetCredentials = {
  user: string | null
  token: string | null
  tokenExpirationAt: string | null
  code?: TWarningPassword | TFailurePassword
  globalRole: string | null
  otpCode?: string
}
type UpdateCode = { code: TWarningPassword | TFailurePassword | undefined }
type UpdateOtpCode = { otpCode?: string }
type UpdateToken = { token: string; tokenExpirationAt: string }
type UpdateTerms = { terms: UserTermsStatus }

export const authSlice = createSlice({
  name: "auth",
  initialState: getPersistentAuth,
  reducers: {
    setCredentials: (
      state,
      {
        payload: { user, token, tokenExpirationAt, code, globalRole, otpCode },
      }: PayloadAction<SetCredentials>,
    ): AuthState => ({
      ...state,
      user,
      token,
      tokenExpirationAt,
      code,
      globalRole,
      otpCode,
    }),
    updateCode: (state, { payload: { code } }: PayloadAction<UpdateCode>): AuthState => ({
      ...state,
      code,
    }),
    updateOtpCode: (
      state,
      { payload: { otpCode } }: PayloadAction<UpdateOtpCode>,
    ): AuthState => ({
      ...state,
      otpCode,
    }),
    updateToken: (
      state,
      { payload: { token, tokenExpirationAt } }: PayloadAction<UpdateToken>,
    ): AuthState => ({
      ...state,
      token,
      tokenExpirationAt,
    }),
    updateTerms: (
      state,
      { payload: { terms } }: PayloadAction<UpdateTerms>,
    ): AuthState => ({
      ...state,
      terms,
    }),
    invalidateCredentials: () => initialState,
  },
})

export const {
  setCredentials,
  updateCode,
  updateOtpCode,
  invalidateCredentials,
  updateToken,
  updateTerms,
} = authSlice.actions

export const authReducer: Reducer<AuthState> = authSlice.reducer
export default authReducer

export const selectCode = (state: RootState) => state.auth.code
export const selectOtpCode = (state: RootState) => state.auth.otpCode
export const selectToken = (state: RootState) => state.auth.token
export const selectTokenExpirationAt = (state: RootState) => state.auth.tokenExpirationAt
export const selectCurrentUser = (state: RootState) => state.auth.user
export const selectCurrentUserGlobalRole = (state: RootState) => state.auth.globalRole
export const selectTerms = (state: RootState) => state.auth.terms

// Store the token in the localStorage, while keeping the reducers as pure functions
export const authMiddleware: Middleware = (store) => (next) => (action) => {
  // console.group(action.type)
  const result = next(action)
  if (typeof action === "object" && action !== null && "type" in action) {
    switch (action.type) {
      case "auth/setCredentials": // fallthrough
      case "auth/updateCode":
      case "auth/updateOtpCode":
      case "auth/updateToken":
      case "auth/updateTerms":
      case "auth/invalidateCredentials": {
        const { auth } = store.getState()
        // console.log(`store: ${JSON.stringify(auth, undefined, 2)}`)
        localStorage.setItem("auth", JSON.stringify(auth))
        // console.log("done")
        break
      }
      default:
        break
    }
  }
  // console.groupEnd()
  return result
}
